import { Link } from "react-router-dom";
import { useState, useEffect } from "react";
import { Car, Shield, Menu, X, ChevronRight } from "lucide-react";
import { scrollToTop, SITE_URL } from "../lib/config";

export function Nav() {
  const [open, setOpen] = useState(false);
  return (
    <header className="sticky top-0 z-50 bg-white/80 backdrop-blur-xl border-b border-slate-200/50 shadow-sm" data-testid="main-nav">
      <div className="max-w-6xl mx-auto px-4 sm:px-6 flex items-center justify-between h-16">
        <Link to="/" onClick={scrollToTop} className="flex items-center gap-2" data-testid="nav-logo">
          <div className="w-8 h-8 bg-sky-700 rounded-lg flex items-center justify-center"><Car size={18} className="text-white" /></div>
          <span className="font-bold text-lg text-slate-900" style={{ fontFamily: 'Outfit, sans-serif' }}>QuotesByCity</span>
        </Link>
        <nav className="hidden md:flex items-center gap-6 text-sm font-medium text-slate-600">
          <Link to="/coverage/liability" onClick={scrollToTop} className="hover:text-sky-700 transition-colors" data-testid="nav-coverage">Coverage Types</Link>
          <Link to="/find-your-city" onClick={scrollToTop} className="hover:text-sky-700 transition-colors" data-testid="nav-cities">Find Your City</Link>
          <Link to="/blog" onClick={scrollToTop} className="hover:text-sky-700 transition-colors" data-testid="nav-blog">Blog</Link>
          <Link to="/states" onClick={scrollToTop} className="hover:text-sky-700 transition-colors" data-testid="nav-states">States</Link>
        </nav>
        <div className="flex items-center gap-3">
          <Link to="/" onClick={() => { setTimeout(() => { const el = document.getElementById('quiz'); if (el) el.scrollIntoView({ behavior: 'smooth' }); }, 100); }} className="hidden md:inline-flex bg-sky-700 text-white rounded-xl px-5 py-2.5 text-sm font-medium hover:bg-sky-800 transition-all shadow-sm" data-testid="nav-quiz-btn">
            <Shield size={16} className="mr-1.5" /> Good Driver Quiz
          </Link>
          <button onClick={() => setOpen(!open)} className="md:hidden p-2 text-slate-600" data-testid="nav-mobile-toggle">
            {open ? <X size={22} /> : <Menu size={22} />}
          </button>
        </div>
      </div>
      {open && (
        <div className="md:hidden bg-white border-t border-slate-200 py-4 px-4 space-y-3" data-testid="mobile-menu">
          {[["Coverage Types", "/coverage/liability"], ["Find Your City", "/find-your-city"], ["Blog", "/blog"], ["States", "/states"]].map(([label, path]) => (
            <Link key={path} to={path} onClick={() => { scrollToTop(); setOpen(false); }} className="block text-sm font-medium text-slate-700 hover:text-sky-700 py-2">{label}</Link>
          ))}
          <Link to="/" onClick={() => { setTimeout(() => { const el = document.getElementById('quiz'); if (el) el.scrollIntoView({ behavior: 'smooth' }); }, 100); setOpen(false); }} className="block bg-sky-700 text-white rounded-xl px-4 py-2.5 text-sm font-medium text-center">Good Driver Quiz</Link>
        </div>
      )}
    </header>
  );
}

export function Footer() {
  return (
    <footer className="bg-slate-900 text-slate-300" data-testid="footer">
      <div className="max-w-6xl mx-auto px-4 sm:px-6 py-16">
        <div className="grid grid-cols-1 md:grid-cols-5 gap-10 mb-12">
          <div>
            <div className="flex items-center gap-2 mb-4">
              <div className="w-8 h-8 bg-sky-600 rounded-lg flex items-center justify-center"><Car size={18} className="text-white" /></div>
              <span className="font-bold text-lg text-white" style={{ fontFamily: 'Outfit, sans-serif' }}>QuotesByCity</span>
            </div>
            <p className="text-sm text-slate-400 leading-relaxed">Free auto insurance education for every US city. Compare rates and understand your coverage.</p>
          </div>
          <div>
            <h4 className="text-sm font-semibold text-white mb-3 uppercase tracking-wider">Coverage Types</h4>
            <div className="space-y-2">
              {[["Liability", "/coverage/liability"], ["Collision", "/coverage/collision"], ["Comprehensive", "/coverage/comprehensive"], ["Uninsured Motorist", "/coverage/uninsured-underinsured"], ["Gap Insurance", "/coverage/gap-insurance"], ["Full Coverage Myth", "/coverage/full-coverage-myth"]].map(([n, p]) => (
                <Link key={p} to={p} onClick={scrollToTop} className="block text-sm text-slate-400 hover:text-white transition-colors">{n}</Link>
              ))}
            </div>
          </div>
          <div>
            <h4 className="text-sm font-semibold text-white mb-3 uppercase tracking-wider">Popular States</h4>
            <div className="space-y-2">
              {[["California", "/states/california"], ["Texas", "/states/texas"], ["Florida", "/states/florida"], ["New York", "/states/new-york"], ["Illinois", "/states/illinois"]].map(([n, p]) => (
                <Link key={p} to={p} onClick={scrollToTop} className="block text-sm text-slate-400 hover:text-white transition-colors">{n}</Link>
              ))}
            </div>
          </div>
          <div>
            <h4 className="text-sm font-semibold text-white mb-3 uppercase tracking-wider">Top Cities</h4>
            <div className="space-y-2">
              {[["New York", "/quotes/new-york-ny"], ["Los Angeles", "/quotes/los-angeles-ca"], ["Houston", "/quotes/houston-tx"], ["Miami", "/quotes/miami-fl"], ["Chicago", "/quotes/chicago-il"]].map(([n, p]) => (
                <Link key={p} to={p} onClick={scrollToTop} className="block text-sm text-slate-400 hover:text-white transition-colors">{n}</Link>
              ))}
            </div>
          </div>
          <div>
            <h4 className="text-sm font-semibold text-white mb-3 uppercase tracking-wider">Resources</h4>
            <div className="space-y-2">
              {[["Insurance Blog", "/blog"], ["Browse by State", "/states"], ["Find Your City", "/find-your-city"], ["Privacy Policy", "/privacy"], ["Terms of Service", "/terms"], ["Disclaimer", "/disclaimer"], ["Accessibility", "/accessibility"]].map(([n, p]) => (
                <Link key={p} to={p} onClick={scrollToTop} className="block text-sm text-slate-400 hover:text-white transition-colors">{n}</Link>
              ))}
              <a href="https://www.lifeinsanswers.com" target="_blank" rel="noopener noreferrer" className="block text-sm text-sky-400 hover:text-sky-300 transition-colors mt-2">Questions About Life Insurance?</a>
            </div>
          </div>
        </div>

        <div className="border-t border-slate-800 pt-8 space-y-4">
          {/* AFFILIATE DISCLOSURE */}
          <div className="bg-slate-800/50 rounded-lg p-4">
            <p className="text-xs text-slate-400 leading-relaxed">
              <strong className="text-slate-300">Affiliate Disclosure:</strong> This site contains affiliate links. We may earn a commission when you click through and purchase a policy through our partner links at no additional cost to you. This helps support our free comparison platform.
            </p>
          </div>

          {/* IMPORTANT NOTICE */}
          <div className="bg-slate-800/50 rounded-lg p-4">
            <p className="text-xs text-slate-400 leading-relaxed">
              <strong className="text-slate-300">Important Notice:</strong> QuotesByCity.com is not an insurance company, agent, or broker. We do not sell, bind, or issue insurance policies. We are an independent educational and referral website that connects consumers with licensed insurance providers. All insurance quotes and policy decisions are made solely by the insurance companies and agents you connect with through our platform. Insurance availability and rates may vary by location, coverage type, and individual circumstances.
            </p>
          </div>

          <p className="text-xs text-slate-500 text-center pt-4">&copy; {new Date().getFullYear()} QuotesByCity.com | All Rights Reserved. QuotesByCity is an independent educational site. We help you understand auto insurance and compare quotes from top-rated providers.</p>
        </div>
      </div>
    </footer>
  );
}

export function CookieConsent() {
  const [show, setShow] = useState(false);
  useEffect(() => { if (!localStorage.getItem("qbc_cookies")) setTimeout(() => setShow(true), 2000); }, []);
  if (!show) return null;
  return (
    <div className="fixed bottom-0 left-0 right-0 z-50 bg-white border-t border-slate-200 shadow-lg p-4" data-testid="cookie-consent">
      <div className="max-w-4xl mx-auto flex flex-col sm:flex-row items-center gap-3">
        <p className="text-sm text-slate-600 flex-1">We use cookies to enhance your experience and serve relevant ads. <Link to="/privacy" className="text-sky-700 underline">Privacy Policy</Link></p>
        <div className="flex gap-2">
          <button onClick={() => { setShow(false); }} className="px-4 py-2 text-sm text-slate-500 hover:text-slate-700" data-testid="cookie-decline">Decline</button>
          <button onClick={() => { localStorage.setItem("qbc_cookies", "1"); setShow(false); }} className="px-4 py-2 text-sm bg-sky-700 text-white rounded-lg hover:bg-sky-800" data-testid="cookie-accept">Accept</button>
        </div>
      </div>
    </div>
  );
}

export function HeroBanner({ title, subtitle, badge }) {
  return (
    <section className="relative overflow-hidden" data-testid="hero-banner">
      <div className="absolute inset-0">
        <img src="https://images.pexels.com/photos/17177648/pexels-photo-17177648.jpeg?auto=compress&cs=tinysrgb&dpr=2&h=650&w=940" alt="" className="w-full h-full object-cover" />
        <div className="absolute inset-0 bg-slate-900/50" />
      </div>
      <div className="relative z-10 max-w-5xl mx-auto px-4 sm:px-6 py-14 sm:py-20 text-center">
        {badge && <span className="inline-block bg-white/10 backdrop-blur-sm border border-white/20 text-white/90 text-xs font-semibold px-4 py-1.5 rounded-full mb-4">{badge}</span>}
        <h1 className="text-3xl sm:text-4xl lg:text-5xl font-bold text-white mb-3 leading-tight" style={{ fontFamily: 'Outfit, sans-serif' }}>{title}</h1>
        {subtitle && <p className="text-base sm:text-lg text-white/70 max-w-2xl mx-auto">{subtitle}</p>}
      </div>
    </section>
  );
}

export function QuizTeaser() {
  const handleClick = () => {
    const el = document.getElementById('quiz');
    if (el) { el.scrollIntoView({ behavior: 'smooth' }); }
    else { window.location.href = '/#quiz'; }
  };
  return (
    <button onClick={handleClick} className="block w-full text-left bg-gradient-to-r from-sky-50 to-amber-50 border border-sky-200 rounded-xl p-5 hover:border-sky-400 transition-all group" data-testid="quiz-teaser">
      <div className="flex items-center gap-4">
        <div className="w-12 h-12 bg-sky-700 rounded-xl flex items-center justify-center shrink-0 group-hover:scale-105 transition-transform">
          <Shield size={22} className="text-white" />
        </div>
        <div className="flex-1 min-w-0">
          <h3 className="font-bold text-slate-900 text-sm" style={{ fontFamily: 'Outfit, sans-serif' }}>Think You're a Good Driver?</h3>
          <p className="text-xs text-slate-500 mt-0.5">Take our 2-minute quiz and find out if you qualify for good driver discounts</p>
        </div>
        <ChevronRight size={20} className="text-sky-700 shrink-0 group-hover:translate-x-1 transition-transform" />
      </div>
    </button>
  );
}
